'use client'
import { EmptyContent } from '@/components/common/empty-content'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, CircularProgress, FormControl, useMediaQuery, useTheme } from '@mui/material'
import type { AutocompleteProps } from '@mui/material/Autocomplete'
import Autocomplete from '@mui/material/Autocomplete'
import type { TextFieldProps } from '@mui/material/TextField'
import TextField from '@mui/material/TextField'
import { useRef } from 'react'
import { Controller, FieldValues, Path, useFormContext, useFormState } from 'react-hook-form'
import { ErrorTooltipIcon, type ErrorTooltipIconRef, FieldLabel } from '../rhf-text-field/components'
import { getErrorBorderStyles } from '../rhf-text-field/styles'

// ----------------------------------------------------------------------

const ITEM_HEIGHT = 40
const ITEM_PADDING_TOP = 8

export type AutocompleteBaseProps = Omit<AutocompleteProps<any, boolean, boolean, boolean>, 'renderInput'>

interface RHFAutocompleteProps<T extends FieldValues> extends AutocompleteBaseProps {
  name: Path<T>
  label?: string | React.ReactNode
  placeholder?: string
  helperText?: React.ReactNode
  required?: boolean
  className?: string
  slotProps?: AutocompleteBaseProps['slotProps'] & {
    textfield?: TextFieldProps
  }
  onChange?: (newValue: any) => void
}

export function RHFAutocomplete<T extends FieldValues>({
  name,
  label,
  slotProps,
  helperText,
  placeholder,
  required,
  className,
  onChange,
  loading,
  ...other
}: RHFAutocompleteProps<T>) {
  const { control } = useFormContext()
  const { isSubmitted } = useFormState({ control })
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const errorIconRef = useRef<ErrorTooltipIconRef>(null)

  const { textfield, ...otherSlotProps } = slotProps ?? {}

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {label && <FieldLabel label={label} required={required} />}
      <Controller
        name={name}
        control={control}
        render={({ field: { value, ...field }, fieldState: { error } }) => {
          const valueOption = other.options?.find((option) => option.value === value) || null

          const handleAutocompleteClick = () => {
            // Ẩn tooltip khi click vào autocomplete
            if (errorIconRef.current) {
              errorIconRef.current.hideTooltip()
            }
          }
          return (
            <FormControl error={!!error} className={`w-full ${className || ''}`}>
              <Autocomplete
                {...field}
                autoHighlight
                disabled={loading}
                value={valueOption}
                disableClearable={loading}
                id={`rhf-autocomplete-${name}`}
                onChange={(e, newValue) => {
                  field.onChange(newValue.value)
                  onChange?.(newValue.value)
                }}
                popupIcon={
                  loading ? (
                    <CircularProgress
                      size={16}
                      sx={{
                        color: 'text.secondary',
                        marginRight: 0.5,
                        marginTop: -0.125,
                        marginLeft: 0.5,
                      }}
                    />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )
                }
                slotProps={{
                  paper: {
                    sx: {
                      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                        '& .MuiAutocomplete-option': {
                          minHeight: ITEM_HEIGHT,
                        },
                      },
                    },
                  },
                  popper: {
                    disablePortal: false,
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          altBoundary: false,
                        },
                      },
                    ],
                  },
                  ...otherSlotProps,
                }}
                noOptionsText={<EmptyContent title="" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...textfield}
                    placeholder={placeholder}
                    error={!!error}
                    onClick={handleAutocompleteClick}
                    // Show helperText only on mobile
                    helperText={!isDesktop ? (error?.message ?? helperText) : helperText}
                    slotProps={{
                      ...textfield?.slotProps,
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: 'new-password',
                        disabled: loading,
                        ...textfield?.slotProps?.htmlInput,
                      },
                      formHelperText: {
                        ...(error && !isDesktop && { error: true }),
                        ...textfield?.slotProps?.formHelperText,
                      },
                      input: {
                        ...params.InputProps,
                        ...textfield?.slotProps?.input,
                        disabled: loading,
                        endAdornment: (
                          <>
                            {/* Error Icon với MUI Tooltip */}
                            {error && (
                              <ErrorTooltipIcon
                                ref={errorIconRef}
                                errorMessage={error.message || ''}
                                showTooltip={isDesktop}
                                isSubmitted={isSubmitted}
                              />
                            )}
                            {/* Giữ lại endAdornment gốc của Autocomplete */}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                    sx={
                      {
                        ...textfield?.sx,
                        ...getErrorBorderStyles(!!error),
                      } as any
                    }
                  />
                )}
                {...other}
              />
            </FormControl>
          )
        }}
      />
    </Box>
  )
}
